/**
 * Stat card for dashboard summaries — displays a title, icon, numeric value, and label.
 *
 * @example
 * import { Folder } from 'lucide-react';
 *
 * <DashboardCard
 *   title="Source Drives"
 *   icon={<Folder className="size-5" />}
 *   value={342}
 *   label="files"
 *   iconColor="text-cyan-500"
 *   iconBg="bg-cyan-50"
 *   onClick={() => navigate('/drives')}
 * />
 */
const DashboardCard = ({
  title,
  icon,
  value,
  label,
  iconColor = 'text-brand-600',
  iconBg = 'bg-brand-50',
  highlighted = false,
  onClick,
  className = '',
  ...props
}) => {
  const Tag = onClick ? 'button' : 'div';

  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={[
        'relative group rounded-lg border-2 p-4 text-left transition-all duration-200',
        highlighted
          ? 'border-brand-500 bg-brand-50 shadow-lg'
          : 'border-edge bg-surface hover:border-brand-300 hover:shadow-md',
        onClick && 'cursor-pointer',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...props}
    >
      <h3 className="text-sm font-semibold text-content mb-3">{title}</h3>

      <div className="flex items-center gap-3">
        {icon && (
          <div
            className={[
              'inline-flex items-center justify-center size-10 rounded-lg flex-shrink-0 transition-transform duration-200 group-hover:scale-110',
              iconBg,
              iconColor,
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {icon}
          </div>
        )}

        <div className="flex items-baseline gap-1.5">
          <span className="text-xl font-bold text-content">
            {typeof value === 'number' ? value.toLocaleString() : value}
          </span>
          {label && (
            <span className="text-xs text-content-muted">{label}</span>
          )}
        </div>
      </div>
    </Tag>
  );
};

export default DashboardCard;
